<template>
  <div>
    <form-base :header="header" hide-required-legend>
      <template #form>
        <v-form ref="form" :class="{ disabled: readOnly }">
          <v-container class="px-8 pt-8" fluid>
            <h2 class="mb-8">
              {{ $t('warehouse.pages.details.headers.addressAndDetails') }}
            </h2>
            <h4 class="mb-6">{{ $t('warehouse.pages.details.headers.contactInfo') }}</h4>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <text-field
                  :disabled="readOnly"
                  data-testid="warehouse-name-field"
                  :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                  :required="true"
                  type="text"
                  v-model="editingWarehouse.name"
                  :rules="
                    $validator.rules.required(
                      $t('warehouse.forms.createWarehouseForm.labels.warehouseName')
                    )
                  "
                  :tooltipText="
                    $t(
                      'warehouses.details.tooltips.nameOfWarehouseLocationVisibleToWarehouseAndCarriers'
                    )
                  "
                  :label="
                    $t('warehouse.forms.createWarehouseForm.labels.warehouseName')
                  "></text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <phone-number-field
                  data-testid="warehouse-phone-number-field"
                  v-if="editingWarehouse?.id"
                  :disabled="readOnly"
                  :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                  margin-top="20px"
                  v-model="editingWarehouse.phone"
                  :validator="$validator"
                  :default-country="defaultPhoneCountry"
                  :tooltipText="
                    $t('warehouse.pages.details.tooltips.warehouseContactPhoneNumberForCarriers')
                  "
                  :label="$t('account.forms.myAccountForm.labels.phone')"></phone-number-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <text-field
                  data-testid="warehouse-email-field"
                  :disabled="readOnly"
                  :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                  type="email"
                  v-model="editingWarehouse.email"
                  :tooltipText="
                    $t(
                      'warehouse.forms.createWarehouseForm.tooltips.warehouseContactEmailForCarriersReplyTo'
                    )
                  "
                  :rules="$validator.rules.email"
                  :label="
                    $t('warehouse.forms.createWarehouseForm.labels.warehouseContactEmail')
                  "></text-field>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <text-field
                  data-testid="warehouse-store-number-field"
                  :disabled="readOnly"
                  :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                  prepend-icon="mdi-pound"
                  v-model="editingWarehouse.facilityNumber"
                  :tooltipText="
                    $t('warehouse.forms.createWarehouseForm.tooltips.warehouseStoreNumber')
                  "
                  :label="
                    $t('warehouse.forms.createWarehouseForm.labels.storeNumber')
                  "></text-field>
              </v-col>
            </v-row>

            <v-row class="mb-4">
              <v-col class="align-field" cols="12" sm="6" md="4">
                <country-select
                  test-id="warehouse-country-select"
                  :disabled="readOnly"
                  v-model="editingAddress.country"
                  limit-options
                  :tooltipText="
                    $t('warehouse.forms.createWarehouseForm.tooltips.countryOfWarehouseLocation')
                  "
                  :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"></country-select>
              </v-col>
              <v-col :class="{ 'align-field': manualAddressMode }">
                <address-autocomplete-field
                  test-id="warehouse-address-field"
                  required
                  ref="searchAddressField"
                  v-if="!manualAddressMode"
                  clear-on-country-change
                  v-model="geocodingData"
                  :readonly="!$rolePermissions.canUpdateWarehouse || readOnly"
                  :disabled="!editingAddress.country || readOnly"
                  :country="editingAddress.country"
                  :tooltipText="
                    $t(
                      'warehouse.forms.createWarehouseForm.tooltips.addressOfWarehouseLocationVisibleToCarriers'
                    )
                  "
                  @filled="fillFullAddress"
                  @manual-override="enterManualAddressMode"></address-autocomplete-field>
                <street-field
                  v-else
                  test-id="warehouse-street-field"
                  ref="manualStreetField"
                  @search="enterSearchAddressMode"
                  required
                  v-model="editingAddress.street"
                  :rules="
                    $validator.rules.required(
                      $t('warehouse.forms.createWarehouseForm.labels.streetAddress')
                    )
                  " />
              </v-col>
            </v-row>

            <address-display
              v-if="!manualAddressMode && editingAddress.city"
              :zip="editingAddress.zip"
              :city="editingAddress.city"
              :state="editingAddress.state"
              :street="editingAddress.street"
              :timezone="editingAddress.timezone" />

            <v-row v-else-if="manualAddressMode">
              <v-col cols="12" sm="6" md="3">
                <text-field
                  required
                  data-testid="warehouse-city-field"
                  type="text"
                  v-model="editingAddress.city"
                  :rules="$validator.rules.required($t('lists.common.headers.city'))"
                  :label="$t('lists.common.headers.city')"></text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <state-select
                  test-id="warehouse-state-select"
                  v-if="editingAddress.country === 'USA' || editingAddress.country === 'CAN'"
                  required
                  :rules="$validator.rules.required($t('lists.common.headers.state'))"
                  :label="$t('lists.common.headers.state')"
                  :country="editingAddress.country"
                  v-model="editingAddress.state"></state-select>
                <text-field
                  data-testid="warehouse-state-field"
                  v-else
                  required
                  type="text"
                  v-model="editingAddress.state"
                  :rules="$validator.rules.required($t('lists.common.headers.state'))"
                  :label="$t('lists.common.headers.state')"></text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <text-field
                  required
                  data-testid="warehouse-zip-field"
                  type="text"
                  v-model="editingAddress.zip"
                  :rules="
                    $validator.rules.required(
                      $t('warehouse.forms.createWarehouseForm.labels.zipPostalCode')
                    )
                  "
                  :placeholder="$t('warehouse.forms.createWarehouseForm.placeholders.zipCode')"
                  :label="
                    $t('warehouse.forms.createWarehouseForm.labels.zipPostalCode')
                  "></text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <timezone-select
                  data-testid="warehouse-timezone-select"
                  v-model="editingAddress.timezone"
                  :rules="
                    $validator.rules.required(
                      $t('warehouse.forms.createWarehouseForm.labels.timezone')
                    )
                  "
                  :label="$t('warehouse.forms.createWarehouseForm.labels.timezone')"
                  required />
              </v-col>
            </v-row>

            <h4 class="mb-2 pt-6">
              {{ $t('warehouse.pages.details.headers.warehouseLogo') }}
              <help-icon-tooltip class="z-100" icon-style-class="grey darken-2">
                {{
                  $t(
                    'core.settings.labels.uploadYourEntityLogoToDisplayItInTheSchedulingPortalAndInAllEmailNotifications',
                    {
                      entity: $t('global.specialtyTerms.warehouses')
                    }
                  )
                }}
              </help-icon-tooltip>
            </h4>
            <div class="d-flex align-center mt-2">
              <document-upload-button
                :read-only="readOnly"
                :crop-shape="warehouseLogoSetting.documentOptions?.cropShape"
                :aspectRatio="warehouseLogoSetting.documentOptions?.aspectRatio"
                :show-crop-result="warehouseLogoSetting.documentOptions?.showCropResult"
                :modal-header-text="warehouseLogoSetting.documentOptions?.modalHeaderText"
                allow-warehouseLogoSetting
                :max-size="warehouseLogoSetting.maxFileSize ?? 1024"
                :conversion-unit="warehouseLogoSetting.fileSizeUnit ?? novaCore.FileUnits.MB"
                :allowed-mime-types="warehouseLogoSetting.allowedMimeTypes"
                class="flex-grow-0"
                v-model="warehouseSettings.customLogo"></document-upload-button>
            </div>
            {{ warehouseLogoSetting.description }}
            <div class="v-text-field__details">
              <div class="v-messages theme--light">
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">{{ warehouseLogoSetting.description }}</div>
                </div>
              </div>
            </div>

            <h4 class="mb-2 pt-6">
              {{ $t('warehouse.pages.details.headers.warehouseEmailSubscribers') }}
              <help-icon-tooltip class="z-100" icon-style-class="grey darken-2">
                {{
                  $t(
                    'warehouse.forms.createWarehouseForm.tooltips.defaultEmailSubscribersForAppointmentsAtThisFacility'
                  )
                }}
              </help-icon-tooltip>
            </h4>
            <v-row>
              <v-col>
                <email-list-field
                  test-id="warehouse-email-list-field"
                  class="full-width mb-4"
                  outlined
                  :label="$t('appointment.forms.createAppointmentForm.labels.emailSubscribers')"
                  :read-only="!$rolePermissions.canUpdateWarehouse || readOnly"
                  :disabled="readOnly"
                  v-model="editingWarehouse.ccEmails"
                  small-chips
                  small></email-list-field>
              </v-col>
            </v-row>

            <h4 class="mb-1 pt-8">
              {{ $t('warehouse.pages.details.headers.warehouseInternalNotes') }}
              <help-icon-tooltip class="z-100" icon-style-class="grey darken-2">
                {{ $t('warehouse.forms.createWarehouseForm.tooltips.notesInternalToWarehouse') }}
              </help-icon-tooltip>
            </h4>
            <v-row>
              <v-col>
                <v-textarea
                  data-testid="warehouse-internal-notes-field"
                  v-if="$rolePermissions.canUpdateWarehouse && !readOnly"
                  :label="$t('forms.elements.inputs.notesField.label')"
                  rows="2"
                  variant="outlined"
                  v-model="editingWarehouse.notes"></v-textarea>
                <div v-else class="v-input v-text-field">
                  <div class="v-text-field__slot full-width">
                    <label class="v-label font-size-x-small">
                      {{ $t('forms.elements.inputs.notesField.label') }}
                    </label>
                    <div class="text--disabled border-width-1 bordered rounded pa-4 full-width">
                      {{ editingWarehouse.notes }}
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <h2 class="mb-2 mt-6">
              {{ $t('warehouse.pages.details.headers.instructionsToCarriers') }}
            </h2>

            <h4 class="mb-2 pt-6">
              {{ $t('warehouse.pages.details.headers.yardMap') }}
              <help-icon-tooltip class="z-100" icon-style-class="grey darken-2">
                {{ $t('core.settings.labels.uploadWarehouseYardMap') }}
              </help-icon-tooltip>
            </h4>
            <div class="d-flex align-center mt-2">
              <document-upload-button
                :read-only="readOnly"
                :crop-shape="warehouseYardMapSetting.documentOptions?.cropShape"
                :aspectRatio="warehouseYardMapSetting.documentOptions?.aspectRatio"
                :show-crop-result="warehouseYardMapSetting.documentOptions?.showCropResult"
                :modal-header-text="warehouseYardMapSetting.documentOptions?.modalHeaderText"
                allow-warehouseYardMapSetting
                :max-size="warehouseYardMapSetting.maxFileSize ?? 1024"
                :conversion-unit="warehouseYardMapSetting.fileSizeUnit ?? novaCore.FileUnits.MB"
                :allowed-mime-types="warehouseYardMapSetting.allowedMimeTypes"
                class="flex-grow-0"
                v-model="warehouseSettings.yardMap"></document-upload-button>
            </div>
            <div class="v-text-field__details">
              <div class="v-messages theme--light">
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">{{ warehouseYardMapSetting.description }}</div>
                </div>
              </div>
            </div>

            <h4 class="mb-5 pt-8">
              {{ $t('warehouse.pages.details.headers.warehouseGeneralInstructions') }}
              <help-icon-tooltip class="z-100" icon-style-class="grey darken-2">
                {{
                  $t(
                    'warehouse.forms.createWarehouseForm.messages.toBeDisplayedInTheEmailNotificationsAndTheCarrierViewPortal'
                  )
                }}
              </help-icon-tooltip>
            </h4>
            <v-row>
              <v-col>
                <rich-text-input
                  v-if="$rolePermissions.canUpdateWarehouse && !readOnly"
                  :label="
                    $t('warehouse.forms.createWarehouseForm.messages.instructionsForCarriers')
                  "
                  @ready="handleRichTextReady"
                  v-model="editingWarehouse.instructions"></rich-text-input>
                <div v-else class="v-input v-text-field">
                  <div class="v-text-field__slot full-width">
                    <div
                      class="text--disabled border-width-1 bordered rounded pa-4"
                      v-html="editingWarehouse.instructions"></div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <warehouse-amenities-ppe-requirements
                  :fields="editingWarehouse"></warehouse-amenities-ppe-requirements>
              </v-col>
            </v-row>

            <action-group
              class="pr-0 pt-8"
              :confirm-label="$t('warehouse.forms.createWarehouseForm.buttons.saveWarehouseInfo')"
              :hide-cancel="true"
              cancel-color="error"
              :confirm-button-tooltip="readOnly ? novaCore.getRoleActionError() : ''"
              :disable-confirm="!$rolePermissions.canUpdateWarehouse || readOnly"
              @confirm="submit"></action-group>
          </v-container>
        </v-form>
      </template>
    </form-base>
  </div>
</template>

<script>
import autocompleteAddressMixin from '@/components/mixins/autocompleteAddressMixin';
import editWarehouseMixin from '@/modules/warehouses/editWarehouseMixin';

/**
 * Edit Warehouse Form
 * @displayName Edit Warehouse Form
 */
export default {
  mixins: [autocompleteAddressMixin, editWarehouseMixin],
  computed: {
    warehouseBaseSettings() {
      return this.novaCore.makeGroupedSettings(this.novaCore.DatabaseEntities.Warehouse, true);
    },
    warehouseLogoSetting() {
      return this.warehouseBaseSettings['Scheduling Portal'].customLogo;
    },
    warehouseYardMapSetting() {
      return this.warehouseBaseSettings['Scheduling Portal'].yardMap;
    }
  },
  beforeMount() {
    this.warehouseSettings = { ...(this.warehouse?.settings || {}) };
  },
  mounted() {
    this.setEditingWarehouse();
  }
};
</script>

<style scoped>
p {
  line-height: 0;
}
</style>
